/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { AgentVerified } from '@atlaskit/rovo-agent-components/agent-verified';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { mapAgentGqlToAgent } from '../../../../common/utils/agent-query-helper/index.ts';

import type { listItem_AtlassianConversationAssistantAgent_AgentListItem$key } from './__generated__/listItem_AtlassianConversationAssistantAgent_AgentListItem.graphql.ts';

type AgentListItemProps = {
	node: listItem_AtlassianConversationAssistantAgent_AgentListItem$key;
	// onClickChat prop can be removed when all consumers are using onClickAgent
	onClickChat?: (agent: Agent) => void;
	onClickAgent?: (agent: Agent) => void;
};

const styles = cssMap({
	listItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		textAlign: 'left',
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

export const AgentListItem = ({
	node,
	onClickChat,
	onClickAgent,
}: AgentListItemProps): JSX.Element => {
	const agentFragmentData = useFragment(
		/* eslint-disable @atlassian/relay/unused-fields */
		/* These fields are used in the mapAgentGqlToAgent function */
		graphql`
			fragment listItem_AtlassianConversationAssistantAgent_AgentListItem on AgentStudioAssistant {
				id
				name
				deactivated
				description
				isFavourite
				creatorType
				conversationStarters
				identityAccountId
				externalConfigReference
				creator {
					id
				}
				icon
				authoringTeam {
					id
					profileUrl
					displayName
					smallAvatarImageUrl
				}
				...agentVerified_AtlaskitRovoAgentComponents
			}
		`,
		node,
	);

	const agent = useMemo(() => mapAgentGqlToAgent(agentFragmentData), [agentFragmentData]);

	const { externalConfigReference, identityAccountId, id, creatorType, icon, name } =
		agentFragmentData;

	return (
		<Pressable
			xcss={styles.listItem}
			onClick={onClickAgent ? () => onClickAgent(agent) : () => onClickChat?.(agent)}
		>
			<AgentAvatar
				agentNamedId={externalConfigReference ?? undefined}
				agentIdentityAccountId={identityAccountId}
				agentId={id}
				size="small"
				showBorder={false}
				isForgeAgent={creatorType === 'FORGE'}
				forgeAgentIconUrl={icon ?? undefined}
			/>
			<Flex alignItems="start" justifyContent="start" gap="space.075">
				<Text>{name ?? ''}</Text>
				{fg('rovo_agents_agent_verification') && <AgentVerified agentRef={agentFragmentData} />}
			</Flex>
		</Pressable>
	);
};
